import React, { useState, useEffect } from "react";
import { useThemeMode } from "../../ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import { dataAPI } from "../../helpers/api_service";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const RolePermission = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  const [permissions, setPermissions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [modulesData, setModulesData] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [roleId, setRoleId] = useState(null);

  // Get data from location state (passed from RoleForm or Roles)
  useEffect(() => {
    if (location.state) {
      // Handle the API response structure
      const modulesData = location.state.modulesData || [];
      const roleName = location.state.roleName || "";
      const roleDescription = location.state.roleDescription || "";
      const isEditMode = location.state.isEditMode || false;
      const roleId = location.state.roleId || null;

      setModulesData(modulesData);
      setRoleName(roleName);
      setRoleDescription(roleDescription);
      setIsEditMode(isEditMode);
      setRoleId(roleId);

      // Initialize permissions using the correct fields from API response
      if (modulesData && modulesData.length > 0) {
        console.log("Initializing permissions with modules data:", modulesData);
        const initialPermissions = {};
        modulesData.forEach((module) => {
          initialPermissions[module.id] = {
            has_read: module.can_read || false,
            has_add: module.can_add || false,
            has_edit: module.can_edit || false,
            has_delete: module.can_delete || false,
          };
        });
        console.log("Initial permissions object:", initialPermissions);
        setPermissions(initialPermissions);
      }
    }
  }, [location.state]);

  const handlePermissionChange = (moduleId, permissionType, value) => {
    setPermissions((prev) => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        [permissionType]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Log immediately when button is clicked (use console.warn so it's visible)
    console.warn("[RolePermission] Submit button clicked", {
      isEditMode,
      roleId,
      roleName,
    });

    // Validation
    if (!roleName || !roleDescription) {
      setError("Role name and description are required");
      console.warn("[RolePermission] Validation failed: role name or description missing");
      return;
    }

    if (!modulesData || modulesData.length === 0) {
      setError("No modules data available");
      console.warn("[RolePermission] Validation failed: no modules data");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Create the submit JSON as per the required format
      const submitData = {
        name: roleName,
        description: roleDescription,
        permissions: modulesData.map((module) => ({
          module_id: module.id,
          can_read: permissions[module.id]?.has_read || false,
          can_add: permissions[module.id]?.has_add || false,
          can_edit: permissions[module.id]?.has_edit || false,
          can_delete: permissions[module.id]?.has_delete || false,
        })),
        role_type: "company",
      };

      let response;
      if (isEditMode && roleId) {
        // Update existing role using the new edit endpoint
        console.warn("[Update Role API] REQUEST", { method: "PUT", roleId, body: submitData });
        response = await dataAPI.editRole(roleId, submitData);
        console.warn("[Update Role API] RESPONSE", response);
      } else {
        console.warn("[Create Role API] REQUEST", { body: submitData });
        response = await dataAPI.createRole(submitData);
        console.warn("[Create Role API] RESPONSE", response);
      }

      // If API returned status "error", stay on page and show error message
      if (response?.status === "error") {
        setError(response?.message || "An error occurred. Please try again.");
        return;
      }

      // Success - navigate back to roles page with success message
      const action = isEditMode ? "updated" : "created";
      navigate("/manage-employee/roles", {
        state: {
          successMessage: `Role "${roleName}" ${action} successfully!`,
        },
      });
    } catch (error) {
      console.error("[Update Role API] ERROR", error);
      const action = isEditMode ? "update" : "create";
      setError(error.message || `Failed to ${action} role. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/manage-employee/roles");
  };

  return (
    <div
      style={{
        padding: "24px",
        width: "100%",
        height: "100%",
        backgroundColor: mode === "dark" ? "#000000" : "#f8f9fa",
        fontFamily: "Urbanist, Arial, sans-serif",
      }}
    >
      {/* Header with Inputs and Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: "32px",
          gap: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "20px",
            flex: "1",
          }}
        >
          {/* Role Name and Description Fields - Only show in edit mode */}
          {isEditMode && (
            <>
              {/* Role Name Field */}
              <div style={{ minWidth: "200px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: mode === "dark" ? "#FFFFFF" : "#222222",
                    fontFamily: "Urbanist, Arial, sans-serif",
                  }}
                >
                  Role Name
                </label>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: mode === "dark" ? "#2a2a2a" : "#ffffff",
                    color: mode === "dark" ? "#FFFFFF" : "#222222",
                    fontFamily: "Urbanist, Arial, sans-serif",
                  }}
                  placeholder="Role name"
                />
              </div>

              {/* Role Description Field */}
              <div style={{ minWidth: "300px", flex: "1" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: mode === "dark" ? "#FFFFFF" : "#222222",
                    fontFamily: "Urbanist, Arial, sans-serif",
                  }}
                >
                  Role Description
                </label>
                <input
                  type="text"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: mode === "dark" ? "#2a2a2a" : "#ffffff",
                    color: mode === "dark" ? "#FFFFFF" : "#222222",
                    fontFamily: "Urbanist, Arial, sans-serif",
                  }}
                  placeholder="Role description"
                />
              </div>
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            flexShrink: 0,
            alignSelf: "flex-end",
          }}
        >
          <button
            onClick={handleBack}
            style={{
              padding: "10px 20px",
              backgroundColor: "transparent",
              color: "#26C3C0",
              border: "1px solid #26C3C0",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "Urbanist, Arial, sans-serif",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#26C3C0";
              e.target.style.color = "#FFFFFF";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#26C3C0";
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              padding: "10px 20px",
              backgroundColor: isSubmitting ? "#666" : "#26C3C0",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              fontFamily: "Urbanist, Arial, sans-serif",
              transition: "background-color 0.2s ease",
              opacity: isSubmitting ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = "#22B0AD";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = "#26C3C0";
              }
            }}
          >
            {isSubmitting
              ? "Submitting..."
              : isEditMode
                ? "Update Role"
                : "Create Role"}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "6px",
            color: "#d32f2f",
            fontSize: "14px",
            fontFamily: "Urbanist, Arial, sans-serif",
          }}
        >
          {error}
        </div>
      )}

      {/* Permissions Table */}
      <div
        style={{
          backgroundColor: mode === "dark" ? "#0f0f0f" : "#ffffff",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow:
            mode === "dark"
              ? "0 2px 8px rgba(0,0,0,0.3)"
              : "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {/* Select All Row */}
        <div
          style={{
            padding: "12px 20px",
            backgroundColor: mode === "dark" ? "#0f0f0f" : "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <input
            type="checkbox"
            checked={
              modulesData.length > 0 &&
              modulesData.every(
                (module) =>
                  permissions[module.id]?.has_read &&
                  permissions[module.id]?.has_add &&
                  permissions[module.id]?.has_edit &&
                  permissions[module.id]?.has_delete,
              )
            }
            onChange={(e) => {
              const isChecked = e.target.checked;
              const newPermissions = {};
              modulesData.forEach((module) => {
                newPermissions[module.id] = {
                  has_read: isChecked,
                  has_add: isChecked,
                  has_edit: isChecked,
                  has_delete: isChecked,
                };
              });
              setPermissions(newPermissions);
            }}
            style={{
              width: "16px",
              height: "16px",
              accentColor: "#26C3C0",
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: mode === "dark" ? "#FFFFFF" : "#222222",
              fontFamily: "Urbanist, Arial, sans-serif",
            }}
          >
            Select All
          </span>
        </div>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Urbanist, Arial, sans-serif",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: mode === "dark" ? "#2a2a2a" : "#f8f9fa",
                borderBottom: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
              }}
            >
              <th
                style={{
                  padding: "12px 20px",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: mode === "dark" ? "#FFFFFF" : "#222222",
                  borderBottom: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                  width: "30%",
                }}
              >
                Menu Name
              </th>
              <th
                style={{
                  padding: "12px 12px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: mode === "dark" ? "#FFFFFF" : "#222222",
                  borderBottom: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                }}
              >
                View
              </th>
              <th
                style={{
                  padding: "12px 12px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: mode === "dark" ? "#FFFFFF" : "#222222",
                  borderBottom: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                }}
              >
                Create
              </th>
              <th
                style={{
                  padding: "12px 12px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: mode === "dark" ? "#FFFFFF" : "#222222",
                  borderBottom: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                }}
              >
                Edit
              </th>
              <th
                style={{
                  padding: "12px 12px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: mode === "dark" ? "#FFFFFF" : "#222222",
                  borderBottom: `1px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                }}
              >
                Delete
              </th>
            </tr>
          </thead>
          <tbody>
            {modulesData &&
              modulesData.map((module, index) => (
                <tr
                  key={module.id}
                  style={{
                    borderBottom: `1px solid ${mode === "dark" ? "#333" : "#e0e0e0"}`,
                    backgroundColor: mode === "dark" ? "#0f0f0f" : "#ffffff",
                  }}
                >
                  <td
                    style={{
                      padding: "12px 20px",
                      fontSize: "14px",
                      color: mode === "dark" ? "#FFFFFF" : "#222222",
                      fontWeight: "500",
                    }}
                  >
                    {module.name}
                  </td>
                  <td
                    style={{
                      padding: "12px 12px",
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={permissions[module.id]?.has_read || false}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          "has_read",
                          e.target.checked,
                        )
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        accentColor: "#26C3C0",
                        cursor: "pointer",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "12px 12px",
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={permissions[module.id]?.has_add || false}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          "has_add",
                          e.target.checked,
                        )
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        accentColor: "#26C3C0",
                        cursor: "pointer",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "12px 12px",
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={permissions[module.id]?.has_edit || false}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          "has_edit",
                          e.target.checked,
                        )
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        accentColor: "#26C3C0",
                        cursor: "pointer",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "12px 12px",
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={permissions[module.id]?.has_delete || false}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          "has_delete",
                          e.target.checked,
                        )
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        accentColor: "#26C3C0",
                        cursor: "pointer",
                      }}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RolePermission;
